import React, { useCallback, useEffect, useState } from 'react';
import logsService from '../../../services/logs.service';
import Loading from '../../common/Loading';
import { Alert, Button, Collapse, Pagination } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
const STATUS_BADGE = {
    EXECUTED: 'logs-status-badge logs-status-badge--executed',
    FAILED: 'logs-status-badge logs-status-badge--failed',
};

function LogsDashboard({ searchPanelOpen = false, onSearchPanelToggle }) {
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [jobName, setJobName] = useState('');
    const [appliedJobName, setAppliedJobName] = useState('');
    const [pagination, setPagination] = useState({
        page: 0,
        size: 20,
        totalElements: 0,
        totalPages: 0,
    });
    const [copiedId, setCopiedId] = useState(null);
    const [expandedRows, setExpandedRows] = useState([]);

    const fetchLogs = useCallback(async () => {
        const controller = new AbortController();

        try {
            setIsLoading(true);
            setError(null);
            setExpandedRows([]);

            const dateParam = selectedDate ? selectedDate.toISOString().split('T')[0] : '';

            const res = await logsService.getLogs(
                pagination.page,
                pagination.size,
                dateParam,
                appliedJobName,
                { signal: controller.signal }
            );

            setLogs(res.data.content || []);
            setPagination((prev) => ({
                ...prev,
                totalElements: res.data.totalElements || 0,
                totalPages: res.data.totalPages || 0,
            }));
        } catch (err) {
            if (err.name !== 'AbortError') {
                setError('No logs found.');
                setLogs([]);
            }
        } finally {
            setIsLoading(false);
        }

        return () => controller.abort();
    }, [pagination.page, pagination.size, selectedDate, appliedJobName]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handleDateChange = (date) => {
        setSelectedDate(date);
        setPagination((prev) => ({ ...prev, page: 0 }));
    };

    const handlePageChange = (newPage) => {
        setPagination((prev) => ({ ...prev, page: newPage }));
    };

    const handleSizeChange = (event) => {
        const newSize = Number(event.target.value);
        setPagination((prev) => ({ ...prev, size: newSize, page: 0 }));
    };

    const clearFilters = () => {
        setSelectedDate(null);
        setJobName('');
        setAppliedJobName('');
        setPagination((prev) => ({ ...prev, page: 0 }));
    };

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        const nextJobName = jobName.trim();
        if (nextJobName === appliedJobName) return;
        setAppliedJobName(nextJobName);
        setPagination((prev) => ({ ...prev, page: 0 }));
    };

    const handleClearJobNameSearch = () => {
        setJobName('');
        if (appliedJobName) {
            setAppliedJobName('');
            setPagination((prev) => ({ ...prev, page: 0 }));
        }
    };

    const copyToClipboard = (id) => {
        navigator.clipboard.writeText(id);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const toggleRow = (id) => {
        setExpandedRows((prev) =>
            prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
        );
    };

    const formatJson = (obj) => {
        if (!obj) return 'N/A';
        try {
            return JSON.stringify(obj, null, 2);
        } catch {
            return 'Invalid JSON';
        }
    };

    const getStatusBadgeClass = (status) =>
        STATUS_BADGE[status] || 'logs-status-badge logs-status-badge--other';

    const getPaginationItems = () => {
        const items = [];
        const currentPage = pagination.page;
        const totalPages = pagination.totalPages;
        const maxVisible = 5;

        if (totalPages < 1) return items;

        items.push(
            <Pagination.Item
                key={1}
                active={currentPage === 0}
                onClick={() => handlePageChange(0)}
                disabled={isLoading}
            >
                1
            </Pagination.Item>
        );

        let startPage = Math.max(2, currentPage);
        let endPage = Math.min(totalPages - 1, currentPage + 2);

        if (currentPage < 3) endPage = Math.min(totalPages - 1, maxVisible);
        if (currentPage > totalPages - 4) startPage = Math.max(2, totalPages - maxVisible + 1);

        if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage + 1}
                    onClick={() => handlePageChange(number - 1)}
                    disabled={isLoading}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);

        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage + 1}
                    onClick={() => handlePageChange(totalPages - 1)}
                    disabled={isLoading}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const startItem = logs.length > 0 ? pagination.page * pagination.size + 1 : 0;
    const endItem = Math.min((pagination.page + 1) * pagination.size, pagination.totalElements);

    const paginationControls =
        pagination.totalPages > 1 ? (
            <Pagination className="mb-0">
                <Pagination.First onClick={() => handlePageChange(0)} disabled={pagination.page === 0 || isLoading} />
                <Pagination.Prev
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 0 || isLoading}
                />
                {getPaginationItems()}
                <Pagination.Next
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages - 1 || isLoading}
                />
                <Pagination.Last
                    onClick={() => handlePageChange(pagination.totalPages - 1)}
                    disabled={pagination.page >= pagination.totalPages - 1 || isLoading}
                />
            </Pagination>
        ) : null;

    return (
        <>
            <div className="d-flex align-items-center justify-content-between gap-2 mb-3 flex-wrap">
                <div className="mb-0 d-flex align-items-center gap-2">
                    <i className="bi bi-list-task text-primary"></i>
          <h5 className="card-title mb-0 logs-card__title">
                        Job History
                        <span className="logs-card__count">
                            ({pagination.totalElements} log{pagination.totalElements !== 1 ? 's' : ''} total)
                        </span>
                    </h5>
                </div>
                <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-auto">
                    <form onSubmit={handleSearchSubmit} className="logs-job-name-search">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text" aria-hidden="true">
                                <i className="bi bi-search"></i>
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by job name…"
                                value={jobName}
                                onChange={(event) => setJobName(event.target.value)}
                                aria-label="Search by job name"
                                disabled={isLoading}
                            />
                            {jobName && (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={handleClearJobNameSearch}
                                    aria-label="Clear job name search"
                                    title="Clear search"
                                    disabled={isLoading}
                                >
                                    <i className="bi bi-x"></i>
                                </button>
                            )}
                        </div>
                    </form>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm flex-shrink-0"
                        onClick={onSearchPanelToggle}
                        aria-expanded={searchPanelOpen}
                        title={searchPanelOpen ? 'Hide filters' : 'Show filters'}
                    >
                        <i className="bi bi-funnel me-1"></i>
                        Filter
                    </button>
                </div>
            </div>

            <Collapse in={searchPanelOpen}>
                <div>
                    <div className="process-queue-filters mb-4 p-3 bg-light rounded border">
                        <div className="row g-2 g-md-3 align-items-center process-queue-filters__row">
                            <div className="col-12 col-md-auto process-queue-filters__item">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text process-queue-filters__label">Date</span>
                                    <div className="logs-date-picker-wrap">
                                        <DatePicker
                                            selected={selectedDate}
                                            onChange={handleDateChange}
                                            dateFormat="yyyy-MM-dd"
                                            className="form-control form-control-sm process-queue-filters__control"
                                            placeholderText="All dates"
                                            maxDate={new Date()}
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="select"
                                        />
                                        {selectedDate && (
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm logs-date-clear text-danger"
                                                title="Clear date filter"
                                                onClick={() => {
                                                    setSelectedDate(null);
                                                    setPagination((prev) => ({ ...prev, page: 0 }));
                                                }}
                                            >
                                                <i className="bi bi-x-lg"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-lg-auto process-queue-filters__actions d-flex flex-nowrap gap-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={clearFilters}
                                    disabled={(!selectedDate && !appliedJobName && !jobName) || isLoading}
                                    title="Clear filters"
                                >
                                    <i className="bi bi-x-lg me-1"></i>
                                    Clear
                                </Button>
                                <Button variant="outline-primary" size="sm" onClick={fetchLogs} disabled={isLoading}>
                                    <i className={`bi bi-arrow-clockwise me-1 ${isLoading ? 'spin' : ''}`}></i>
                                    Refresh
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </Collapse>

            {isLoading ? (
                <Loading />
            ) : error && logs.length === 0 ? (
                <Alert variant="warning" className="border-0 shadow-sm">
                    {error}
                </Alert>
            ) : (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle mb-0 process-queue-table logs-table">
                        <thead>
                            <tr>
                                <th>
                                    <i className="bi bi-list-task"></i> Job Name
                                </th>
                                <th>
                                    <i className="bi bi-calendar-date"></i> Execution Date &amp; Time
                                </th>
                                <th>
                                    <i className="bi bi-activity"></i> Status
                                </th>
                                <th>
                                    <i className="bi bi-stopwatch"></i> Time Taken
                                </th>
                                <th className="text-end">
                                    <i className="bi bi-gear"></i> Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center text-muted py-5">
                                        No scheduled logs found.
                                    </td>
                                </tr>
                            ) : (
                                logs.map((log) => (
                                    <React.Fragment key={log.id}>
                                        <tr>
                                            <td>{log.name}</td>
                                            <td>{log.startTime || log.executionDate}</td>
                                            <td>
                                                <span className={getStatusBadgeClass(log.status)}>
                                                    {log.status}
                                                </span>
                                            </td>
                                            <td>{log.timeTaken}s</td>
                                            <td className="text-end">
                                                <Button
                                                    variant={
                                                        expandedRows.includes(log.id)
                                                            ? 'outline-secondary'
                                                            : 'outline-primary'
                                                    }
                                                    size="sm"
                                                    onClick={() => toggleRow(log.id)}
                                                    title={expandedRows.includes(log.id) ? 'Collapse' : 'Expand'}
                                                >
                                                    <i
                                                        className={`bi bi-chevron-${
                                                            expandedRows.includes(log.id) ? 'up' : 'down'
                                                        }`}
                                                    ></i>
                                                </Button>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan={5} className="p-0 border-top-0">
                                                <Collapse in={expandedRows.includes(log.id)}>
                                                    <div className="logs-detail-panel">
                                                        <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                                                            <span className="small">
                                                                <strong>ID:</strong>{' '}
                                                                <span className="font-monospace">{log.id}</span>
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="logs-copy-btn"
                                                                onClick={() => copyToClipboard(log.id)}
                                                                title="Copy ID"
                                                            >
                                                                <i
                                                                    className={`bi ${
                                                                        copiedId === log.id
                                                                            ? 'bi-check2 text-success'
                                                                            : 'bi-copy'
                                                                    }`}
                                                                ></i>
                                                            </button>
                                                        </div>
                                                        <div className="row g-2 mb-3 small">
                                                            <div className="col-md-6">
                                                                <strong>Start Time:</strong> {log.startTime}
                                                            </div>
                                                            <div className="col-md-6">
                                                                <strong>End Time:</strong> {log.endTime}
                                                            </div>
                                                            <div className="col-md-4">
                                                                <strong>Email Sent:</strong>{' '}
                                                                {log.isEmailResult ? 'Yes' : 'No'}
                                                            </div>
                                                            <div className="col-md-4">
                                                                <strong>Email Status:</strong>{' '}
                                                                {log.emailStatus || 'N/A'}
                                                            </div>
                                                        </div>
                                                        <div className="small">
                                                            <strong>Result:</strong>
                                                            <pre className="logs-detail-pre mt-2">{formatJson(log.result)}</pre>
                                                        </div>
                                                    </div>
                                                </Collapse>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <span className="small text-muted">Show</span>
                        <select
                            className="form-select form-select-sm logs-page-size"
                            value={pagination.size}
                            onChange={handleSizeChange}
                            disabled={isLoading}
                        >
                            {[10, 20, 50].map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                        <span className="small text-muted">entries</span>
                    </div>
                    <span className="text-muted small">
                        Showing {startItem} to {endItem} of {pagination.totalElements}
                    </span>
                </div>
                {paginationControls}
            </div>
        </>
    );
}

export default LogsDashboard;
